import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, SimpleGrid, Alert, AlertIcon, Button } from "@chakra-ui/react";
import theme from "../../theme"; // Adjust the import path if necessary
import "./Dashboard.css"; // Import the CSS file for hover effects and styling
import Chart from "../ChartComponents/Chart";
import BarChart from "../ChartComponents/BarChart";
import RadarChart from "../ChartComponents/RadarChart";
import LineChart from "../ChartComponents/LineChart";
import { cqAPI } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";
import moment from "moment";
import ProgressBar from '../ChartComponents/ProgressBar';

// Map conversation topics to 1-word categories
const topicCategoryMapping = {
  "Appointment Management": "Appointments",
  "Appointments and Visits": "Appointments",
  "Treatment and Services": "Treatment",
  "Personal Data Handling": "Data",
  "Medical Records": "Data",
  "Billing and Payments": "Billing",
  "Insurance Handling": "Insurance",
  "Promotions and Offers": "Promotions",
  "Customer Service": "Service",
  "Communication Preferences": "Service",
  "Health and Safety": "Health",
  "Dental Health": "Health",
  "COVID-19 Specific": "COVID",
  "Legal and Compliance": "Compliance",
  "Technology and Equipment": "Technology",
  "Product Information": "Product",
  "Prescriptions": "Prescriptions",
};

const lines = [
  { name: "CurrentYear", dataKey: "CurrentYear", stroke: theme.colors.brand[30] },
  { name: "PreviousYear", dataKey: "PreviousYear", stroke: theme.colors.brand[20] },
];

export default function Dashboard() {
  const { isPatient } = useAppContext();

  const [alertData, setAlertData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await cqAPI.get(`/dashboard`);
        if (response.type === "ok") {
          setDashboardData(response.data);
        } else {
          throw new Error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    };

    const fetchAlertData = async () => {
      try {
        if (isPatient){
          return
        }
        const response = await cqAPI.get(`/threads?openonly=1&type=email,sms,emailsms,voicemail,api`);
        if (response.type !== "ok") {
          throw new Error("Invalid response for dashboard alert");
        }
        const conversationDates = response.data.map(item => moment(item.updated));
        const oldestConversation = conversationDates.reduce((min, current) => (current < min ? current : min), conversationDates[0]);
        const now = moment();
        const daysSinceOldestConv = now.diff(oldestConversation, 'days');

        setAlertData({
          numOpenConversations: conversationDates.length,
          daysSinceOldestConversation: daysSinceOldestConv,
        });
      } catch (error) {
        console.error("Failed to fetch alert data:", error);
      }
    };

    fetchDashboardData();
    fetchAlertData();
  }, []);

  const mapTopicsToCategories = (histogram, topN = 4) => {
    const categoryCounts = Object.entries(histogram || {}).reduce((acc, [topic, value]) => {
      const category = topicCategoryMapping[topic] || topic;
      acc[category] = (acc[category] || 0) + value;
      return acc;
    }, {});

    const categoryArray = Object.entries(categoryCounts).map(([name, value]) => ({ name, value }));
    categoryArray.sort((a, b) => b.value - a.value);

    const topCategories = categoryArray.slice(0, topN);
    const otherCategories = categoryArray.slice(topN);
    const othersValue = otherCategories.reduce((acc, curr) => acc + curr.value, 0);

    if (othersValue > 0) {
      topCategories.push({ name: "Others", value: othersValue });
    }

    return topCategories;
  };

  const transformSatisfactionData = (data) => {
    const fullMark = 100;
    return Object.entries(data || {}).map(([subject, value]) => ({
      subject,
      A: (value || 0).toFixed(2),
      fullMark,
    }));
  };

  const formatStaffDashboardData = (data) => {
    const formattedCommunicationData = Object.entries(data.communicationData || {}).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
    }));

    const formattedCommonTopics = mapTopicsToCategories(data.commonTopics, 3);

    const formattedResponseTimes = Object.entries(data.responseTimes || {}).map(([name, value]) => ({
      name,
      ResponseTime: Math.round((value || 0) / 3600 * 100) / 100, // convert seconds to hours
    }));

    const formattedExtCalls = (data.extCalls || []).map(({ numericValue, totalCalls }) => ({
      name: String(numericValue),
      value: totalCalls,
    }));

    const formattedStaffPerformance = (data.staffPerformance || []).map(({ name, sms, email }) => {
      const initials = name ? name.split(' ').map(word => word[0].toUpperCase()).join('') : '';
      const initialShort = initials.length <= 2 ? initials : initials[0] + initials[initials.length - 1];
      return { fullname: name, name: initialShort, SMS: sms, Emails: email };
    });

    const patientSatisfaction = transformSatisfactionData(data.patientSatisfaction);

    return {
      communicationData: formattedCommunicationData,
      appointmentData: data.appointmentData || [],
      commonTopics: formattedCommonTopics,
      responseTimes: formattedResponseTimes,
      extCalls: formattedExtCalls,
      staffPerformance: formattedStaffPerformance,
      patientSatisfaction,
      revenueTrends: data.revenueTrends || {},
      collectionTrends : data.collectionTrends || {}
    };
  };

  const formatPatientDashboardData = (data) => {
    const healthScore = data.healthScoreData?.length > 0 ? data.healthScoreData[0]?.value : 0;
    return {
      appointmentData: data.appointmentData || [],
      healthScoreData: data.healthScoreData || [],
      treatmentHistory: data.treatmentHistory || [],
      accountBalanceData: data.accountBalanceData || [],
      insuranceCoverageData: data.insuranceCoverageData || [],
      healthScoreTrend: data.healthScoreTrend || [],
      gaugeData: [
        { name: "Value", value: healthScore },
        { name: "Remaining", value: 100 - healthScore },
      ],
    };
  };

  if (!dashboardData) {
    return <Text>Loading...</Text>;
  }

  const patientAlerts = dashboardData?.patientAlerts;

  return (
    <Box className="dashboard-container">
      <Box className="dashboard-padding">
        <Text className="page-title dashboard-title">
          Dashboard (last 30 days)
        </Text>
        {!isPatient && alertData && (
          <Box display="flex" justifyContent="center" className="alert-info">
            <Alert status="info" variant="info">
              <Box className="alert-icon-wrapper">
                <AlertIcon />
              </Box>
              {`Staff needs to respond to ${alertData.numOpenConversations} emails and SMS messages`}
              {alertData.daysSinceOldestConversation
                ? ` older than ${alertData.daysSinceOldestConversation} days.`
                : `.`}
            </Alert>
          </Box>
        )}
        {isPatient && dashboardData.pending_forms > 0 && (
          <Box display="flex" justifyContent="center" className="alert-info">
            <Alert status="info" variant="info">
              <Box className="alert-icon-wrapper">
                <AlertIcon />
              </Box>
              You have {dashboardData.pending_forms} forms to review and sign.
              <Button marginLeft="10px" onClick={() => navigate('/forms' + window.location.search)}>
                Go to Forms
              </Button>
            </Alert>
          </Box>
        )}
        {patientAlerts && patientAlerts.map((alertObject,index)=>(
          <Box display="flex" justifyContent="center" className="alert-info" key={index}>
            <Alert status="info" variant="info">
              <Box className="alert-icon-wrapper">
                <AlertIcon />
              </Box>
          {alertObject.msg}
            </Alert>
          </Box>
        ))}
        {isPatient ? (
          <PatientDashboard data={formatPatientDashboardData(dashboardData)} />
        ) : (
          <StaffDashboard data={formatStaffDashboardData(dashboardData)} />
        )}
      </Box>
    </Box>
  );
}

function StaffDashboard({ data }) {
  const staffPerformanceBars = [
    { dataKey: 'SMS', stackId: 'a', fill: theme.colors.brand[30], name: 'SMS' },
    { dataKey: 'Emails', stackId: 'a', fill: theme.colors.brand[20], name: 'Emails' },
  ];

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 5, md: 10 }}>
      {data.communicationData && data.communicationData.some(x => ['Calls', 'Sms', 'Email', 'Voicemail'].includes(x.name) && x.value !== null && x.value > 0) && (
        <Chart data={data.communicationData} title="Patient Communication Overview" />
      )}
      {data.commonTopics && data.commonTopics.length > 0 && (
        <Chart data={data.commonTopics} title="Common Topics Discussed" />
      )}
      {data.appointmentData && data.appointmentData.length > 0 && (
        <Chart data={data.appointmentData} title="Appointment Statuses" />
      )}
      {data.responseTimes && data.responseTimes.length > 0 && data.responseTimes?.some(x=> x.ResponseTime>0) &&(
        <BarChart
          data={data.responseTimes}
          title="Response Time Metrics (hours)"
          bars={[{ dataKey: 'ResponseTime', fill: theme.colors.brand[30], name: 'Response Time' }]}
          yLabel="hours"
        />
      )}
      {data.extCalls && data.extCalls.length > 0 && (
        <BarChart
          data={data.extCalls}
          title="Staff Performance (Phone Extensions)"
          bars={[{ dataKey: 'value', fill: theme.colors.brand[30], name: 'Calls' }]}
          yLabel="number of calls"
        />
      )}
      {data.staffPerformance && data.staffPerformance.length > 0 && (
        <BarChart
          data={data.staffPerformance}
          title="Staff Performance Metrics"
          bars={staffPerformanceBars}
          yLabel="number of messages"
        />
      )}
      {data.patientSatisfaction && data.patientSatisfaction.length > 0 && data.patientSatisfaction?.some(x=> x.A > 0) && (
        <RadarChart data={data.patientSatisfaction} title="Patient Satisfaction Indicators" />
      )}
      {data.revenueTrends && Object.keys(data.revenueTrends).length !== 0 && (data.revenueTrends['CurrentYear'].length > 0 || data.revenueTrends['PreviousYear'].length > 0) && (
        <LineChart data={data.revenueTrends} title="Producton Trends Comparison" lines={lines} />
      )}
      {data.collectionTrends && Object.keys(data.collectionTrends).length !== 0 && (data.collectionTrends['CurrentYear'].length > 0 || data.collectionTrends['PreviousYear'].length > 0) && (
        <LineChart data={data.collectionTrends} title="Collection Trends Comparison" lines={lines} />
      )}
    </SimpleGrid>
  );
}

function PatientDashboard({ data }) {
  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 5, md: 10 }}>
      {data.appointmentData && data.appointmentData.length > 0 && (
        <Chart
          data={data.appointmentData}
          title="Appointment Statuses"
          caption="This chart shows the status of your appointments. The sections represent upcoming, completed, and missed appointments."
        />
      )}
      {data.healthScoreData && (
        <ProgressBar
          data={data.gaugeData}
          title="Oral Health Score"
          caption="This gauge shows your overall oral health score. It is a percentage that indicates your current oral health status."
        />
      )}
      {data.treatmentHistory && data.treatmentHistory.length > 0 && (
        <Chart
          data={data.treatmentHistory}
          title="Treatment History"
          caption="This chart provides an overview of your treatment history, showing the types and quantities of treatments you have received."
        />
      )}
      {data.accountBalanceData && data.accountBalanceData.length > 0 && (
        <Chart
          data={data.accountBalanceData}
          title="Account Balance"
          caption="This chart shows your account balance, indicating the amounts paid and outstanding for your dental treatments."
        />
      )}
      {data.insuranceCoverageData && data.insuranceCoverageData.length > 0 && (
        <Chart
          data={data.insuranceCoverageData}
          title="Insurance Coverage"
          caption="This chart illustrates your insurance coverage, showing the used and remaining benefits for the current period."
        />
      )}
      {data.healthScoreTrend && Object.keys(data.healthScoreTrend).length > 0 && (
        <LineChart
          data={data.healthScoreTrend}
          title="Health Score Trend"
          lines={lines}
          caption="This line chart shows the trend of your oral health score over time, helping you track improvements or changes in your oral health."
        />
      )}
    </SimpleGrid>
  );
}
